import { legacy_createStore as createStore, applyMiddleware, Store } from 'redux';
import { createEpicMiddleware } from 'redux-observable';
import { AnyAction } from 'typescript-fsa';
import { createRouterMiddleware } from '@lagunovsky/redux-react-router';
import { composeWithDevToolsDevelopmentOnly } from '@redux-devtools/extension';
import { browserHistory } from '@cbridge-utils/app';
import { clientApiService } from '@cbridge-packages/api';
import { notificationApiService } from '@cbridge-packages/api-notifications';
import { conversionApiService } from '@cbridge-packages/api-conversion';
import { supersetApiService } from '@cbridge-packages/api-superset';
import { collectorApiService } from '@cbridge-packages/api-collector';
import { markoutApiService } from '@cbridge-packages/api-markout';
import { coverageApiService } from '@cbridge-packages/api-coverage';
import { ClpApiService, clpApiService } from '@cbridge-packages/api-clp';
import { AuthService } from '@cbridge-packages/auth';
import { rootEpic } from './rootEpic';
import { createRootReducer } from './rootReducer';

async function createAuthService(): Promise<AuthService> {
  if (import.meta.env['VITE_MOCK_API']) {
    const { MockAuthService } = await import('../../../../.mock/auth/MockAuthService');
    return new MockAuthService() as unknown as AuthService;
  }
  return new AuthService({
    url: import.meta.env['VITE_KEYCLOAK_URL'] as string,
    realm: import.meta.env['VITE_KEYCLOAK_REALM'] as string,
    clientId: import.meta.env['VITE_KEYCLOAK_CLIENT'] as string,
  });
}

// In mock mode swap in an in-memory CLP transport (MSW's WebSocket interception is not reliable
// here). The real service needs auth: CLP reads the JWT from a `token` cookie on the WS upgrade
// (LB-3633), which the service writes before each connect. Same DI pattern as createAuthService.
async function createClpApiService(authService: AuthService): Promise<ClpApiService> {
  if (import.meta.env['VITE_MOCK_API']) {
    const { MockClpApiService } = await import('../../../../.mock/clp/MockClpApiService');
    return new MockClpApiService() as unknown as ClpApiService;
  }
  clpApiService.setAuth(authService);
  return clpApiService;
}

export const makeStore = async (): Promise<Store> => {
  const reducers = createRootReducer(browserHistory);

  const authService = await createAuthService();
  const clpApi = await createClpApiService(authService);

  clientApiService.setAuth(authService);
  notificationApiService.setAuth(authService);
  conversionApiService.setAuth(authService);
  supersetApiService.setAuth(authService);
  collectorApiService.setAuth(authService);
  markoutApiService.setAuth(authService);
  coverageApiService.setAuth(authService);

  const dependencies = {
    api: clientApiService,
    nsApi: notificationApiService,
    csApi: conversionApiService,
    supersetApi: supersetApiService,
    clApi: collectorApiService,
    moApi: markoutApiService,
    coverageApi: coverageApiService,
    clpApi,
    auth: authService,
  };

  type Dependencies = typeof dependencies;

  // Create router middleware for Redux integration
  const routerMiddleware = createRouterMiddleware(browserHistory);

  // Create epic middleware for handling side effects
  const epicMiddleware = createEpicMiddleware<AnyAction, AnyAction, ReturnType<typeof reducers>, Dependencies>({
    dependencies,
  });

  const store = createStore(
    reducers,
    {},
    composeWithDevToolsDevelopmentOnly(applyMiddleware(routerMiddleware, epicMiddleware)),
  );

  epicMiddleware.run(rootEpic);

  // @ts-expect-error fork the @lagunovsky/redux-react-router and fix the type
  return store;
};
